<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">Painel de execuções</h1>
          <p class="text-slate-400 mt-1 text-sm">
            Visão por agente; atualiza a cada {{ pollSeconds }}s.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 px-4 py-2 rounded-lg border border-cyan-400/45 text-cyan-100 text-sm font-medium hover:bg-cyan-400/10 transition"
          @click="goRunLogs"
        >
          Registo de logs
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">{{ loadError }}</div>

      <div v-if="loading && !cards.length" class="text-center py-16 text-slate-400">Carregando…</div>

      <template v-else>
        <div v-if="cards.length" class="mb-6 flex flex-col lg:flex-row flex-wrap gap-3">
          <label class="sr-only" for="dashboard-agent-filter">Buscar agente</label>
          <input
            id="dashboard-agent-filter"
            v-model="robotFilter"
            type="search"
            placeholder="Buscar por nome, descrição ou número do agente…"
            class="flex-1 min-w-[min(100%,16rem)] px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-slate-100 placeholder:text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            autocomplete="off"
          />
          <div
            v-if="coordinatorGroups.length > 0"
            class="w-full sm:w-auto sm:min-w-[200px] lg:min-w-[220px]"
          >
            <label class="sr-only" for="dashboard-coordinator-group-filter">Grupo de coordenadores</label>
            <select
              id="dashboard-coordinator-group-filter"
              v-model="filterCoordinatorGroupId"
              class="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="">Todos os grupos</option>
              <option v-for="g in coordinatorGroups" :key="g.id" :value="String(g.id)">{{ g.name }}</option>
            </select>
          </div>
          <div class="w-full sm:w-auto sm:min-w-[200px] lg:min-w-[220px]">
            <label class="sr-only" for="dashboard-status-filter">Filtrar por status</label>
            <select
              id="dashboard-status-filter"
              v-model="statusFilter"
              class="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            >
              <option value="all">Todos os status</option>
              <option value="running">Em execução</option>
              <option value="idle">Ocioso</option>
            </select>
          </div>
        </div>

        <div v-if="filteredCards.length" class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            v-for="row in filteredCards"
            :key="row.id"
            type="button"
            class="text-left rounded-xl border-2 border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 p-5 hover:border-cyan-400/70 hover:bg-slate-900/90 transition"
            :aria-busy="!!row.active_run"
            @click="openDrawer(row)"
          >
            <div class="flex items-start gap-4">
              <div
                class="flex-shrink-0 rounded-full"
                :class="row.active_run ? 'agent-dashboard-avatar--running p-0.5' : ''"
                :title="row.active_run ? 'Agente em execução' : ''"
              >
                <AgentAvatar
                  :variant="row.avatar_variant"
                  :color="row.avatar_color"
                  size="xl"
                  :name="row.name"
                  eyes-pulse
                />
              </div>
              <div class="min-w-0 flex-1">
                <h2 class="font-semibold text-slate-100">{{ row.name }}</h2>
                <p
                  class="text-xs text-slate-400 line-clamp-3 break-words mt-0.5"
                  :title="dashboardAgentDescriptionTitle(row)"
                >
                  {{ row.description || '—' }}
                </p>
                <div class="mt-3 space-y-2">
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-if="row.active_run"
                      class="inline-flex items-center gap-2 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
                    >
                      <span class="relative inline-flex h-2 w-2">
                        <span class="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80 animate-ping" />
                        <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                      </span>
                      <span>Em execução #{{ row.active_run.id }}</span>
                    </span>
                    <span v-else class="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-600">Ocioso</span>
                  </div>
                  <p class="text-xs text-slate-400 leading-snug">
                    {{ cardStatusLine(row) }}
                  </p>
                </div>
              </div>
            </div>
          </button>
        </div>
        <p v-else-if="!loading && cards.length === 0" class="text-center py-12 text-slate-400">
          Nenhum agente cadastrado. Crie agentes em <strong class="text-cyan-300">Agentes</strong>.
        </p>
        <p v-else-if="!loading && cards.length > 0" class="text-center py-12 text-slate-400">
          Nenhum agente corresponde aos filtros.
        </p>
      </template>
    </div>

    <!-- Drawer -->
    <div
      v-if="drawerAgent"
      class="fixed inset-0 z-[85] flex justify-end"
      aria-modal="true"
    >
      <div class="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px]" @click="closeDrawer"></div>
      <div
        class="relative w-full max-w-lg bg-[#435b7c]/95 border-l border-cyan-400/25 shadow-2xl shadow-black/50 h-full overflow-y-auto flex flex-col animate-slide-in [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
      >
        <div class="sticky top-0 bg-[#2f4563] border-b border-cyan-200/25 px-4 py-3 flex items-center justify-between gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <AgentAvatar
              :variant="drawerAgent.avatar_variant"
              :color="drawerAgent.avatar_color"
              size="md"
              :name="drawerAgent.name"
              eyes-pulse
            />
            <div class="min-w-0">
              <h3 class="font-semibold text-slate-100 truncate">{{ drawerAgent.name }}</h3>
              <p class="text-xs text-slate-400">
                {{ drawerAgent.agent_type === 'coordinator' ? 'Execuções via este coordenador' : 'Execuções recentes' }}
              </p>
            </div>
          </div>
          <button type="button" class="p-2 rounded-lg hover:bg-[#3c5576]/80 text-slate-400 hover:text-cyan-300 transition" @click="closeDrawer" aria-label="Fechar">
            ✕
          </button>
        </div>

        <div class="p-4 flex-1 space-y-4">
          <div v-if="runsError" class="text-sm text-red-300">{{ runsError }}</div>
          <ul v-if="runs.length" class="space-y-2">
            <li v-for="r in runs" :key="r.id">
              <button
                type="button"
                class="w-full text-left px-3 py-2 rounded-lg border transition"
                :class="selectedRunId === r.id ? 'border-cyan-300/70 bg-cyan-400/10 shadow-[0_0_0_1px_rgba(34,211,238,0.2)]' : 'border-cyan-200/30 bg-[#3a5172]/45 hover:border-cyan-200/45 hover:bg-[#3c5576]/75'"
                @click="selectRun(r.id)"
              >
                <div class="flex justify-between gap-2 items-center">
                  <span class="text-sm font-medium text-slate-100 min-w-0 truncate">
                    #{{ r.id }} – {{ r.trigger }}
                    <template v-if="drawerAgent && drawerAgent.agent_type === 'coordinator' && r.agent_name">
                      <span class="text-slate-400 font-normal"> · {{ r.agent_name }}</span>
                    </template>
                  </span>
                  <span
                    class="text-xs shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border"
                    :class="
                      r.status === 'running'
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/35'
                        : r.status === 'succeeded'
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                          : r.status === 'failed'
                            ? 'bg-red-500/10 text-red-300 border-red-400/25'
                            : 'bg-slate-800/80 text-slate-300 border-slate-600'
                    "
                  >
                    {{ runStatusLabel(r.status) }}
                    <i
                      v-if="r.status === 'succeeded'"
                      class="fas fa-check text-emerald-300 text-[0.7rem] leading-none"
                      aria-hidden="true"
                    />
                    <span
                      v-else-if="r.status === 'running'"
                      class="agent-run-row-pulse-dot inline-block h-2 w-2 rounded-full bg-emerald-400 shrink-0"
                      aria-hidden="true"
                    />
                  </span>
                </div>
                <div class="text-xs text-slate-400 mt-1 leading-snug">
                  início {{ formatIsoTime(r.started_at || r.created_at) }}
                  <span class="text-slate-600 mx-1.5">·</span>
                  término {{ r.finished_at ? formatIsoTime(r.finished_at) : '—' }}
                </div>
              </button>
            </li>
          </ul>
          <div
            v-if="runsPagy && runsPagy.count > 0"
            class="flex flex-col gap-3 pt-3 mt-1 border-t border-slate-700/70"
          >
            <p class="text-xs text-slate-400">
              {{ runsPagy.from }}–{{ runsPagy.to }} de {{ runsPagy.count }}
              {{ runsPagy.count === 1 ? 'execução' : 'execuções' }}
            </p>
            <div v-if="runsPagy.pages > 1" class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="px-2.5 py-1 text-xs rounded-md border border-slate-600 text-slate-300 hover:bg-[#3c5576]/70 disabled:opacity-40 disabled:pointer-events-none"
                :disabled="runsLoading || !runsPagy.prev"
                @click="goRunsPage(runsPagy.prev)"
              >
                Anterior
              </button>
              <div class="flex flex-wrap items-center gap-1">
                <button
                  v-for="btn in runsPageButtons"
                  :key="`runs-p-${btn}`"
                  type="button"
                  class="min-w-[2rem] px-2 py-1 text-xs rounded-md border transition disabled:opacity-40 disabled:pointer-events-none"
                  :class="
                    btn === runsPagy.page
                      ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-200'
                      : 'border-slate-600 text-slate-300 hover:bg-[#3c5576]/70'
                  "
                  :disabled="runsLoading"
                  @click="goRunsPage(btn)"
                >
                  {{ btn }}
                </button>
              </div>
              <button
                type="button"
                class="px-2.5 py-1 text-xs rounded-md border border-slate-600 text-slate-300 hover:bg-[#3c5576]/70 disabled:opacity-40 disabled:pointer-events-none"
                :disabled="runsLoading || !runsPagy.next"
                @click="goRunsPage(runsPagy.next)"
              >
                Próxima
              </button>
              <span class="text-xs text-slate-500 tabular-nums ml-auto sm:ml-0">
                Pág. {{ runsPagy.page }}/{{ runsPagy.pages }}
              </span>
            </div>
          </div>
          <p v-if="!runs.length && !runsLoading" class="text-sm text-slate-400">Nenhuma execução ainda.</p>
          <p v-if="runsLoading" class="text-sm text-slate-400">Carregando execuções…</p>

          <div v-if="runDetail" class="border-t border-slate-700/70 pt-4">
            <h4 class="text-sm font-semibold text-slate-200 mb-3">Detalhe #{{ runDetail.id }}</h4>
            <p v-if="runDetail.error_message" class="text-xs text-red-300 mb-3 rounded-md border border-red-400/30 bg-red-950/30 px-2 py-1.5">
              <strong>Erro:</strong> {{ runDetail.error_message }}
            </p>

            <!-- Entrada -->
            <div class="mb-3 rounded-lg border border-slate-600/60 bg-[#2f4563]/50 p-3 space-y-2 text-xs text-slate-300">
              <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Entrada</p>
              <div v-if="inputUserMessage()" class="text-slate-200">
                <p class="text-slate-400 mb-0.5 font-medium">Mensagem desta execução</p>
                <pre class="mt-0 text-slate-100 whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed">{{ inputUserMessage() }}</pre>
              </div>
              <p v-if="runInputObject().conversation_id" class="text-slate-300">
                <strong class="text-slate-400">Sessão:</strong>
                <span class="ml-1 font-mono text-[11px] text-cyan-100/90 break-all">{{ runInputObject().conversation_id }}</span>
              </p>
              <p v-if="coordinatorDisplayLabel()" class="text-slate-300">
                <strong class="text-slate-400">Coordenador:</strong> {{ coordinatorDisplayLabel() }}
              </p>
              <div
                v-if="splitBatchFromInput()"
                class="mt-2 rounded-md border border-amber-300/35 bg-amber-950/25 p-2 space-y-1.5"
              >
                <p class="text-[11px] uppercase tracking-wide text-amber-200/90 font-semibold">Lote split (coordenador)</p>
                <p class="text-slate-200">
                  <strong class="text-slate-400">Parte:</strong> {{ splitBatchFromInput().partIndex }} / {{ splitBatchFromInput().partsTotal }}
                </p>
                <p class="text-slate-200">
                  <strong class="text-slate-400">Extração do contexto global:</strong>
                  <span class="ml-1 font-mono text-[11px] text-amber-100/90">{{ splitBatchFromInput().source }}</span>
                </p>
                <details v-if="splitBatchFromInput().sharedText" class="rounded border border-slate-600/40 bg-slate-900/30 px-2 py-1">
                  <summary class="cursor-pointer text-amber-200/90 hover:text-amber-100 text-[11px] select-none">Texto injetado em todos os subpedidos</summary>
                  <pre class="mt-1 text-slate-200 whitespace-pre-wrap break-words text-[12px] leading-relaxed max-h-48 overflow-y-auto">{{ splitBatchFromInput().sharedText }}</pre>
                </details>
                <details class="rounded border border-slate-600/40 bg-slate-900/30 px-2 py-1">
                  <summary class="cursor-pointer text-slate-400 hover:text-slate-300 text-[11px] select-none">Mensagem original do usuário (antes do split)</summary>
                  <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words text-[12px] leading-relaxed max-h-40 overflow-y-auto">{{ splitBatchFromInput().originalMessage || '—' }}</pre>
                </details>
              </div>
              <details v-if="inputConversationHistory().length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
                <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
                  Histórico da conversa ({{ inputConversationHistory().length }} mensagens)
                </summary>
                <ul class="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1 border-t border-slate-600/40 pt-2">
                  <li
                    v-for="(turn, idx) in inputConversationHistory()"
                    :key="`h-${idx}`"
                    class="rounded border border-slate-600/40 bg-[#1e293b]/60 p-2"
                  >
                    <span
                      class="inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded mb-1"
                      :class="
                        (turn.role || '').toString().toLowerCase() === 'user'
                          ? 'bg-sky-500/20 text-sky-200'
                          : 'bg-violet-500/20 text-violet-200'
                      "
                    >{{ historyRoleLabel(turn.role) }}</span>
                    <pre class="text-slate-200 whitespace-pre-wrap break-words font-sans text-[12px] leading-snug mt-1">{{ turn.content }}</pre>
                  </li>
                </ul>
              </details>
              <details v-if="Object.keys(inputOtherFields()).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
                <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
                  Metadados / roteamento
                </summary>
                <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words text-[11px] leading-relaxed max-h-48 overflow-y-auto">{{ prettyJson(inputOtherFields()) }}</pre>
              </details>
              <details class="rounded-md border border-slate-600/40 bg-slate-950/30 px-2 py-1">
                <summary class="cursor-pointer text-slate-400 hover:text-slate-300 select-none py-1 text-[11px]">
                  JSON bruto da entrada
                </summary>
                <pre class="mt-2 text-slate-400 whitespace-pre-wrap break-words text-[10px] leading-relaxed max-h-56 overflow-y-auto font-mono">{{ prettyJson(runDetail.input_payload) }}</pre>
              </details>
            </div>

            <!-- Saída -->
            <div class="mb-4 rounded-lg border border-slate-600/60 bg-[#2f4563]/50 p-3 space-y-2 text-xs text-slate-300">
              <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Saída</p>
              <div v-if="hasStructuredOutput()" class="flex flex-wrap gap-2 items-center">
                <span
                  v-if="runOutputObject().provider"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-200 border border-emerald-400/25"
                >{{ runOutputObject().provider }}</span>
                <span
                  v-if="runOutputObject().model"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-400/25"
                >{{ runOutputObject().model }}</span>
              </div>
              <details v-if="runOutputObject().text != null && String(runOutputObject().text).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
                <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1 font-medium">
                  Resposta do assistente (vai ao chat)
                </summary>
                <pre class="mt-1 text-slate-100 whitespace-pre-wrap break-words font-sans text-[13px] leading-relaxed max-h-80 overflow-y-auto border border-slate-600/40 rounded-md p-2 bg-slate-950/35">{{ runOutputObject().text }}</pre>
              </details>
              <p v-else-if="!hasAnyOutputPayload()" class="text-slate-500 italic">Sem saída registrada ainda.</p>
              <p v-else class="text-slate-400">
                Não há <span class="font-mono text-slate-300">text</span> na saída; use <strong class="text-slate-300">Outros campos</strong> ou o JSON bruto.
              </p>
              <details v-if="Object.keys(outputExtraFields()).length" class="rounded-md border border-slate-600/50 bg-slate-900/25 px-2 py-1">
                <summary class="cursor-pointer text-cyan-200/95 hover:text-cyan-100 select-none py-1">
                  Outros campos da saída
                </summary>
                <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words text-[11px] leading-relaxed max-h-48 overflow-y-auto">{{ prettyJson(outputExtraFields()) }}</pre>
              </details>
              <details v-if="hasAnyOutputPayload()" class="rounded-md border border-slate-600/40 bg-slate-950/30 px-2 py-1">
                <summary class="cursor-pointer text-slate-400 hover:text-slate-300 select-none py-1 text-[11px]">
                  JSON bruto da saída
                </summary>
                <pre class="mt-2 text-slate-400 whitespace-pre-wrap break-words text-[10px] leading-relaxed max-h-56 overflow-y-auto font-mono">{{ prettyJson(runDetail.output_payload) }}</pre>
              </details>
            </div>
            <ol class="mt-4 space-y-2">
              <li
                v-for="s in runDetail.steps || []"
                :key="s.id"
                class="text-xs border border-slate-600/70 rounded-lg p-2 bg-[#2f4563]/70"
              >
                <span class="font-mono text-cyan-300">{{ s.sequence }}. {{ s.kind }}</span>
                <div
                  v-if="s.kind === 'split_batch_context'"
                  class="mt-2 rounded-md border border-amber-300/30 bg-slate-900/40 p-2 space-y-1.5 text-slate-200"
                >
                  <p class="text-[11px] uppercase text-amber-200/90 font-semibold">Auditoria — contexto do split</p>
                  <p class="text-xs">
                    <strong>Parte:</strong> {{ splitBatchStepPayload(s).part_index }} / {{ splitBatchStepPayload(s).parts_total }}
                    <span class="text-slate-500 mx-1">·</span>
                    <strong>Fonte:</strong> {{ splitBatchStepPayload(s).shared_constraints_source || '—' }}
                  </p>
                  <details v-if="splitBatchStepPayload(s).shared_constraints_text" class="text-xs">
                    <summary class="cursor-pointer text-amber-200/90 hover:text-amber-100">Contexto compartilhado (preview no log)</summary>
                    <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{{ splitBatchStepPayload(s).shared_constraints_text }}</pre>
                  </details>
                  <details v-if="splitBatchStepPayload(s).original_user_message_preview" class="text-xs">
                    <summary class="cursor-pointer text-slate-400 hover:text-slate-300">Mensagem original (preview)</summary>
                    <pre class="mt-1 text-slate-400 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">{{ splitBatchStepPayload(s).original_user_message_preview }}</pre>
                  </details>
                </div>
                <div
                  v-if="isApiToolResultStep(s)"
                  class="mt-2 rounded-md border border-cyan-300/30 bg-slate-900/40 p-2 space-y-2"
                >
                  <p class="text-[11px] uppercase tracking-wide text-cyan-200/90 font-semibold">Chamada da API</p>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-1 text-slate-200">
                    <p><strong>Nome:</strong> {{ apiToolResultPayload(s).name || '—' }}</p>
                    <p><strong>Operation key:</strong> {{ apiToolResultPayload(s).operation_key || '—' }}</p>
                    <p><strong>Endpoint ID:</strong> {{ apiToolResultPayload(s).endpoint_id ?? '—' }}</p>
                    <p><strong>HTTP status:</strong> {{ apiToolResultPayload(s).http_status ?? '—' }}</p>
                  </div>
                  <p class="text-slate-200 break-all">
                    <strong>URL:</strong> {{ apiToolResultPayload(s).request_url || '—' }}
                  </p>
                  <div>
                    <p class="text-slate-200 mb-1"><strong>Parâmetros enviados (request_payload):</strong></p>
                    <pre class="text-slate-300 whitespace-pre-wrap break-words">{{ prettyJson(apiToolResultPayload(s).request_payload) }}</pre>
                  </div>
                  <details v-if="apiToolResultPayload(s).response_payload != null" class="group">
                    <summary class="cursor-pointer text-cyan-200 hover:text-cyan-100">
                      Ver response_payload
                    </summary>
                    <pre class="mt-1 text-slate-300 whitespace-pre-wrap break-words">{{ prettyJson(apiToolResultPayload(s).response_payload) }}</pre>
                  </details>
                </div>
                <details v-if="s.kind === 'tool_result'" class="mt-2 rounded-md border border-slate-600/45 bg-slate-950/30 px-2 py-1">
                  <summary class="cursor-pointer text-slate-300 hover:text-slate-200 select-none text-[11px]">
                    Ver JSON completo do tool_result
                  </summary>
                  <pre class="mt-2 text-slate-300 whitespace-pre-wrap break-words max-h-72 overflow-y-auto">{{ jsonPreview(s.payload) }}</pre>
                </details>
                <pre v-else class="mt-1 text-slate-300 whitespace-pre-wrap break-words">{{ jsonPreview(s.payload) }}</pre>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { navigateTo } from '../router.js'
import { useApi } from '../composables/useApi'
import { useAgentUi } from '../composables/useAgentUi'
import AgentAvatar from './AgentAvatar.vue'

/** Intervalo de polling: menos pedidos ao servidor vs. UI um pouco mais “lenta” a refletir estado. */
const DASHBOARD_POLL_MS = 8000
const DRAWER_POLL_MS = 8000

export default {
  components: { AgentAvatar },

  setup() {
    const { get } = useApi()
    const { runStatusLabel, runStatusClass, formatIsoTime, formatIsoTimeCompact, jsonPreview } = useAgentUi()
    return { get, runStatusLabel, runStatusClass, formatIsoTime, formatIsoTimeCompact, jsonPreview }
  },

  data() {
    return {
      pollSeconds: DASHBOARD_POLL_MS / 1000,
      cards: [],
      coordinatorGroups: [],
      filterCoordinatorGroupId: '',
      robotFilter: '',
      statusFilter: 'all',
      loading: true,
      loadError: null,
      pollTimer: null,
      drawerAgent: null,
      runs: [],
      runsPage: 1,
      runsPagy: null,
      runsLoading: false,
      runsError: null,
      selectedRunId: null,
      runDetail: null,
      drawerPollTimer: null
    }
  },

  computed: {
    filteredCards() {
      let rows = this.cards
      const q = this.robotFilter.trim().toLowerCase()
      if (q) {
        rows = rows.filter((r) => {
          const name = (r.name || '').toLowerCase()
          const desc = (r.description || '').toLowerCase()
          const idStr = String(r.id)
          return name.includes(q) || desc.includes(q) || idStr.includes(q)
        })
      }
      if (this.statusFilter === 'running') {
        rows = rows.filter((r) => !!r.active_run)
      } else if (this.statusFilter === 'idle') {
        rows = rows.filter((r) => !r.active_run)
      }
      if (this.filterCoordinatorGroupId) {
        const gid = Number(this.filterCoordinatorGroupId)
        if (Number.isFinite(gid) && gid > 0) {
          rows = rows.filter((r) => {
            const ids = Array.isArray(r.coordinator_group_ids) ? r.coordinator_group_ids : []
            return ids.some((x) => Number(x) === gid)
          })
        }
      }
      return rows
    },
    /** Páginas a mostrar no drawer de execuções (janela em torno da atual). */
    runsPageButtons() {
      const p = this.runsPagy
      if (!p || p.pages <= 1) return []
      const total = p.pages
      const cur = p.page
      const out = []
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) out.push(i)
      return out
    }
  },

  mounted() {
    this.startDashboardPoll({ immediate: true })
  },

  beforeUnmount() {
    this.stopDashboardPoll()
    clearInterval(this.drawerPollTimer)
  },

  methods: {
    dashboardAgentDescriptionTitle(row) {
      if (!row) return undefined
      const d = String(row.description || '').trim()
      return d || undefined
    },

    /** Com o drawer aberto, o poll do dashboard fica pausado — só atualizamos a lista de runs do agente. */
    stopDashboardPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    startDashboardPoll({ immediate = false } = {}) {
      this.stopDashboardPoll()
      if (immediate) {
        this.refreshDashboard()
      }
      this.pollTimer = setInterval(() => this.refreshDashboard(), DASHBOARD_POLL_MS)
    },

    async refreshDashboard() {
      const { data, error } = await this.get('/api/v1/agents/dashboard')
      if (error) {
        if (!this.cards.length) this.loadError = 'Não foi possível carregar o painel.'
        return
      }
      this.loadError = null
      this.cards = data.data || []
      this.coordinatorGroups = Array.isArray(data.coordinator_groups) ? data.coordinator_groups : []
      this.loading = false
    },

    goRunLogs() {
      navigateTo('agent-run-logs', {})
    },

    closeDrawer() {
      clearInterval(this.drawerPollTimer)
      this.drawerPollTimer = null
      this.drawerAgent = null
      this.runs = []
      this.runsPage = 1
      this.runsPagy = null
      this.selectedRunId = null
      this.runDetail = null
      this.startDashboardPoll({ immediate: true })
    },

    openDrawer(row) {
      this.stopDashboardPoll()
      this.drawerAgent = row
      this.runsPage = 1
      this.runsPagy = null
      this.selectedRunId = null
      this.runDetail = null
      this.loadRunsForDrawer()
      clearInterval(this.drawerPollTimer)
      this.drawerPollTimer = setInterval(() => this.loadRunsForDrawer(), DRAWER_POLL_MS)
    },

    async loadRunsForDrawer(options = {}) {
      if (!this.drawerAgent) return
      const page = options.page != null ? Math.max(1, Number(options.page)) : this.runsPage
      this.runsLoading = true
      this.runsError = null
      const q =
        this.drawerAgent.agent_type === 'coordinator'
          ? `coordinator_agent_id=${this.drawerAgent.id}`
          : `agent_id=${this.drawerAgent.id}`
      const { data, error } = await this.get(`/api/v1/agent_runs?${q}&page=${page}`)
      this.runsLoading = false
      if (error) {
        this.runsError = 'Falha ao carregar execuções.'
        this.runsPagy = null
        return
      }
      this.runs = data.data || []
      this.runsPagy = data.pagy || null
      this.runsPage = this.runsPagy?.page ?? page
      if (this.selectedRunId) {
        const still = this.runs.some((r) => r.id === this.selectedRunId)
        if (still) this.selectRun(this.selectedRunId)
      }
    },

    goRunsPage(page) {
      if (page == null || this.runsLoading || !this.drawerAgent) return
      this.selectedRunId = null
      this.runDetail = null
      this.loadRunsForDrawer({ page })
    },

    async selectRun(id) {
      this.selectedRunId = id
      const { data, error } = await this.get(`/api/v1/agent_runs/${id}`)
      if (error) {
        this.runDetail = null
        return
      }
      this.runDetail = data
    },

    stepPayload(step) {
      const payload = step?.payload
      if (payload && typeof payload === 'object') return payload
      if (typeof payload !== 'string') return {}
      try {
        const parsed = JSON.parse(payload)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (_) {
        return {}
      }
    },

    isApiToolResultStep(step) {
      if (!step || step.kind !== 'tool_result') return false
      const payload = this.stepPayload(step)
      return payload.source === 'api_endpoint'
    },

    apiToolResultPayload(step) {
      return this.stepPayload(step)
    },

    prettyJson(value) {
      if (value == null) return '—'
      if (typeof value === 'string') return value
      try {
        return JSON.stringify(value, null, 2)
      } catch (_) {
        return String(value)
      }
    },

    parsedRunPayload(raw) {
      if (raw == null) return {}
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) return { ...raw }
      if (typeof raw === 'string') {
        try {
          const p = JSON.parse(raw)
          return p && typeof p === 'object' && !Array.isArray(p) ? p : {}
        } catch (_) {
          return {}
        }
      }
      return {}
    },

    runInputObject() {
      return this.parsedRunPayload(this.runDetail?.input_payload)
    },

    runOutputObject() {
      return this.parsedRunPayload(this.runDetail?.output_payload)
    },

    inputUserMessage() {
      const o = this.runInputObject()
      const t = (o.message || o.query || '').toString().trim()
      return t.length ? t : null
    },

    inputConversationHistory() {
      const h = this.runInputObject().conversation_history
      return Array.isArray(h) ? h : []
    },

    inputOtherFields() {
      const o = this.runInputObject()
      const skip = new Set([ 'message', 'query', 'conversation_id', 'conversation_history', 'split_batch' ])
      return Object.keys(o).reduce((acc, k) => {
        if (!skip.has(k)) acc[k] = o[k]
        return acc
      }, {})
    },

    splitBatchFromInput() {
      const raw = this.runInputObject().split_batch
      if (!raw || typeof raw !== 'object') return null
      const partIndex = raw.part_index ?? raw['part_index']
      const partsTotal = raw.parts_total ?? raw['parts_total']
      if (partIndex == null || partsTotal == null) return null
      return {
        partIndex: Number(partIndex),
        partsTotal: Number(partsTotal),
        source: (raw.shared_constraints_source ?? raw['shared_constraints_source'] ?? '—').toString(),
        sharedText: (raw.shared_constraints_text ?? raw['shared_constraints_text'] ?? '').toString(),
        originalMessage: (raw.original_user_message ?? raw['original_user_message'] ?? '').toString()
      }
    },

    splitBatchStepPayload(step) {
      return this.stepPayload(step)
    },

    outputExtraFields() {
      const o = this.runOutputObject()
      const skip = new Set([ 'text', 'model', 'provider' ])
      return Object.keys(o).reduce((acc, k) => {
        if (!skip.has(k)) acc[k] = o[k]
        return acc
      }, {})
    },

    historyRoleLabel(role) {
      const r = (role || '').toString().toLowerCase()
      if (r === 'user') return 'Usuário'
      if (r === 'assistant') return 'Assistente'
      return (role || '—').toString()
    },

    coordinatorDisplayLabel() {
      const input = this.runInputObject()
      const cid =
        input.coordinator_id != null && input.coordinator_id !== ''
          ? Number(input.coordinator_id)
          : Number(this.runDetail?.coordinator_agent_id)
      if (!Number.isFinite(cid) || cid <= 0) return null

      if (this.drawerAgent && Number(this.drawerAgent.id) === cid && this.drawerAgent.name) {
        return `${this.drawerAgent.name} (id ${cid})`
      }

      const row = (this.cards || []).find((c) => Number(c.id) === cid)
      if (row && row.name) return `${row.name} (id ${cid})`

      return `id ${cid}`
    },

    hasStructuredOutput() {
      const o = this.runOutputObject()
      return !!(o.provider || o.model)
    },

    hasAnyOutputPayload() {
      const raw = this.runDetail?.output_payload
      if (raw == null) return false
      if (typeof raw === 'object' && !Array.isArray(raw)) return Object.keys(raw).length > 0
      if (typeof raw === 'string') return raw.trim().length > 0
      return true
    },

    /** Linha abaixo do badge: execução atual ou última execução registrada. */
    cardStatusLine(row) {
      if (row.active_run) {
        const ar = row.active_run
        const t = ar.started_at || ar.created_at
        return t ? `Iniciado: ${this.formatIsoTimeCompact(t)}` : 'Iniciado: —'
      }
      if (row.last_run) {
        const lr = row.last_run
        const label = this.runStatusLabel(lr.status)
        let t = lr.finished_at
        if (!t) t = lr.started_at || lr.created_at
        const timePart = t ? ` · ${this.formatIsoTimeCompact(t)}` : ''
        return `Última execução: ${label}${timePart}`
      }
      return 'Última execução: nenhuma ainda'
    }
  }
}
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
.animate-slide-in {
  animation: slide-in 0.2s ease-out;
}

/* Pulse no avatar quando há run em `running` (painel Execuções) */
@media (prefers-reduced-motion: no-preference) {
  .agent-dashboard-avatar--running {
    animation: agent-dashboard-avatar-pulse 2s ease-out infinite;
    border-radius: 9999px;
    box-shadow:
      0 0 0 2px rgba(34, 197, 94, 0.55),
      0 0 18px rgba(34, 197, 94, 0.22);
  }
}

/* Só “onda” para fora: anel junto ao avatar → expande e desvanece; pausa; repete (sem fase de voltar). */
@keyframes agent-dashboard-avatar-pulse {
  0% {
    box-shadow:
      0 0 0 2px rgba(34, 197, 94, 0.65),
      0 0 18px rgba(34, 197, 94, 0.22);
  }
  42% {
    box-shadow:
      0 0 0 14px rgba(34, 197, 94, 0.0),
      0 0 26px rgba(34, 197, 94, 0.0);
  }
  42.01%,
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
}

/* Bolinha com pulso na lista de execuções (running) */
@media (prefers-reduced-motion: no-preference) {
  .agent-run-row-pulse-dot {
    animation: agent-run-row-dot-pulse 1.4s ease-out infinite;
  }
}

@keyframes agent-run-row-dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55);
    opacity: 1;
  }
  40% {
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
    opacity: 0.85;
  }
  40.01%,
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    opacity: 1;
  }
}
</style>
